import React from "react";
import { useSelector } from "react-redux";
import { makeStyles, darken } from "@material-ui/core/styles";
import type { Theme } from "src/theme";
import { Button, Tooltip } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import {
  userPlanSelector,
  isImpersonationSessionSelector,
} from "src/store/system/selector";
import { getUserEmailSync } from "src/config";

export interface SubscriptionDetailsProps {
  subscriptionType: string;
  billingEmail: string;
  accountEmail: string;
  onClick: () => void;
  /** Hide Manage Plan during accountant impersonation (billing is owner-only). */
  hideManagePlan?: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === "dark";
  const bgColor = theme.palette.custom.custom.bg;

  return {
    subscriptionWrapper: {
      color: theme.palette.custom.custom.accent,
      textAlign: "left",
    },
    subscriptionTitle: {
      flex: 1,
    },
    emailLine: {
      display: "block",
    },
    managePlan: isDark
      ? {
          backgroundColor: "transparent",
          color: theme.palette.custom.custom.accent,
          border: `1px solid ${theme.palette.custom.custom.accent}`,
          "&:hover": {
            backgroundColor: `${theme.palette.custom.custom.accent}14`, // ~8% opacity
          },
        }
      : {
          backgroundColor: bgColor,
          color: theme.palette.custom.custom.accent,
          "&:hover": {
            backgroundColor: darken(bgColor, 0.2),
          },
        },
  };
});

/** Presentational subscription block for the nav footer. */
export function SubscriptionDetails({
  subscriptionType,
  billingEmail,
  accountEmail,
  onClick,
  hideManagePlan = false,
}: SubscriptionDetailsProps) {
  const recordEvent = useRecordEvent();

  const classes = useStyles();

  return (
    <Box display="flex" flexDirection="column" pl={2} pr={2}>
      <Box
        display="flex"
        className={classes.subscriptionWrapper}
        width="100%"
        alignItems="center"
        pb={1}
      >
        <Typography
          variant="h6"
          component="p"
          className={classes.subscriptionTitle}
        >
          {subscriptionType} Plan
        </Typography>
      </Box>
      <Tooltip title={billingEmail}>
        <Box
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="caption" className={classes.emailLine} noWrap>
            Billing Email: {billingEmail}
          </Typography>
        </Box>
      </Tooltip>
      <Tooltip title={accountEmail}>
        <Box
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Typography variant="caption" className={classes.emailLine} noWrap>
            Account Email: {accountEmail}
          </Typography>
        </Box>
      </Tooltip>
      {!hideManagePlan && (
        <Box py={1} display="flex" justifyContent="flex-start">
          <Button
            variant="contained"
            className={classes.managePlan}
            onClick={() => {
              onClick();
              recordEvent({
                type: "feature-adoption",
                feature: "navigation-selection",
                element: "manage-plan-icon",
              });
            }}
          >
            Manage Plan
          </Button>
        </Box>
      )}
    </Box>
  );
}

function SubscriptionDetailsConnected({ onClick }: { onClick: () => void }) {
  const plan = useSelector(userPlanSelector);
  const hideManagePlan = useSelector(isImpersonationSessionSelector);
  const email = getUserEmailSync();

  return (
    <SubscriptionDetails
      subscriptionType={
        plan === "ultimate"
          ? "Ultimate"
          : plan === "premium"
          ? "Premium"
          : "Basic"
      }
      billingEmail={email}
      accountEmail={email}
      hideManagePlan={hideManagePlan}
      onClick={onClick}
    />
  );
}

export default SubscriptionDetailsConnected;
